import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { networkManager } from '../api/NetworkManager';

interface LiveSession {
  sessionId: string;
  status: 'active' | 'paused' | 'ended';
  currentTeamId: string | null;
  currentTeamIndex: number;
  teamOrder: Array<{
    teamId: string;
    teamName: string;
    themeName?: string;
    status: 'waiting' | 'presenting' | 'completed';
  }>;
  timerState: 'running' | 'paused' | 'stopped' | 'expired';
  timerRemainingSeconds: number;
  timerDurationSeconds: number;
}

function formatTime(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const m = Math.floor(clamped / 60);
  const s = clamped % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function LiveDisplayPage() {
  const { eventId } = useParams();
  const [session, setSession] = useState<LiveSession | null>(null);
  const [localRemaining, setLocalRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSession = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await networkManager.get<{ status: boolean; data: LiveSession }>(
        `/api/events/${eventId}/live?display=true`,
      );
      setSession(res.data);
      setLocalRemaining(res.data.timerRemainingSeconds);
    } catch {
      // Session not available yet
    }
  }, [eventId]);

  // Initial load
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Poll every 1 second for display
  useEffect(() => {
    pollRef.current = setInterval(fetchSession, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchSession]);

  // Local countdown
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!session || session.timerState !== 'running') return;

    timerRef.current = setInterval(() => {
      setLocalRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [session?.timerState, session?.timerRemainingSeconds]);

  // Determine colors
  const isExpired = session?.timerState === 'expired' || localRemaining <= 0;
  const isWarning = !isExpired && localRemaining <= 30;
  const bgColor = isExpired
    ? 'bg-red-900'
    : isWarning
      ? 'bg-yellow-900'
      : 'bg-gray-950';
  const timerColor = isExpired
    ? 'text-red-400'
    : isWarning
      ? 'text-yellow-400'
      : 'text-green-400';
  const pulseClass = isExpired ? 'animate-pulse' : '';

  const currentTeam = session?.teamOrder.find((t) => t.status === 'presenting');
  const completedCount = session?.teamOrder.filter((t) => t.status === 'completed').length ?? 0;
  const totalCount = session?.teamOrder.length ?? 0;

  // No session — waiting screen
  if (!session || !currentTeam) {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-gray-950">
        <img src="/logo.svg" alt="Byte" className="mb-8 h-16 opacity-60" />
        <p className="text-2xl font-medium text-gray-400">
          {session?.status === 'ended'
            ? 'Session Complete'
            : 'Waiting for session to start...'}
        </p>
        {/* Byte logo bottom-right */}
        <div className="absolute bottom-6 right-8">
          <img src="/logo.svg" alt="Byte" className="h-10 opacity-30" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-screen w-screen flex-col items-center justify-center transition-colors duration-700 ${bgColor}`}
    >
      {/* Progress */}
      <div className="absolute top-8 left-0 right-0 flex justify-center">
        <p className="rounded-full bg-white/10 px-6 py-2 text-lg font-medium text-white/70">
          Team {completedCount + 1} of {totalCount}
        </p>
      </div>

      {/* Team Name */}
      <h1 className="mt-4 text-center font-display text-6xl font-extrabold tracking-tight text-white sm:text-7xl lg:text-8xl">
        {currentTeam.teamName}
      </h1>

      {/* Theme */}
      {currentTeam.themeName && (
        <p className="mt-4 text-center text-2xl font-medium text-white/60 sm:text-3xl">
          {currentTeam.themeName}
        </p>
      )}

      {/* Timer */}
      <p
        className={`mt-10 font-mono text-[10rem] font-bold leading-none tracking-tight sm:text-[12rem] lg:text-[14rem] ${timerColor} ${pulseClass}`}
      >
        {formatTime(localRemaining)}
      </p>

      {/* Timer state label */}
      {session.timerState === 'paused' && (
        <p className="mt-4 text-2xl font-semibold uppercase tracking-widest text-yellow-400/80">
          Paused
        </p>
      )}
      {session.timerState === 'stopped' && (
        <p className="mt-4 text-2xl font-semibold uppercase tracking-widest text-gray-400/80">
          Ready
        </p>
      )}

      {/* Byte logo bottom-right */}
      <div className="absolute bottom-6 right-8">
        <img src="/logo.svg" alt="Byte" className="h-10 opacity-30" />
      </div>
    </div>
  );
}
